import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideUpOverlay } from '../SlideUpOverlay';
import { isRtlLocale, normalizeLocale } from '../../i18n/config';

export interface PeakTrendsSelectOption {
  value: string;
  label: string;
  /** Optional section title — first option in each group renders a heading. */
  group?: string;
}

interface PeakTrendsSelectFieldProps {
  /** Visible field label above the trigger. Omit for inline toolbar layouts. */
  label?: string;
  value: string;
  options: PeakTrendsSelectOption[];
  onChange: (value: string) => void;
  sheetTitle?: string;
  className?: string;
  disabled?: boolean;
}

export function PeakTrendsSelectField({
  label,
  value,
  options,
  onChange,
  sheetTitle,
  className = '',
  disabled = false,
}: PeakTrendsSelectFieldProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dir = isRtlLocale(normalizeLocale(i18n.language)) ? 'rtl' : 'ltr';

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;
  const a11yLabel = sheetTitle ?? label ?? selectedLabel;

  const groupedOptions = useMemo(() => {
    const out: Array<{ kind: 'heading'; text: string } | { kind: 'option'; option: PeakTrendsSelectOption }> = [];
    let lastGroup: string | undefined;
    for (const option of options) {
      if (option.group && option.group !== lastGroup) {
        out.push({ kind: 'heading', text: option.group });
        lastGroup = option.group;
      }
      out.push({ kind: 'option', option });
    }
    return out;
  }, [options]);

  const handlePick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <>
      <div className={`peak-trends-select ${className}`.trim()}>
        {label ? <span className="nm-input-label">{label}</span> : null}
        <button
          type="button"
          className="peak-trends-select__trigger"
          onClick={() => setOpen(true)}
          disabled={disabled}
          aria-label={a11yLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="peak-trends-select__value">{selectedLabel}</span>
          <span className="peak-trends-select__chevron" aria-hidden>
            ▾
          </span>
        </button>
      </div>

      <SlideUpOverlay
        open={open}
        onClose={() => setOpen(false)}
        zIndex={3500}
        panelClassName="peak-trends-select__sheet"
        dir={dir}
        aria-label={a11yLabel}
      >
        <div className="mobile-sheet__grab" aria-hidden />
        <div className="peak-trends-select__sheet-head">
          <span className="peak-trends-select__sheet-title">{a11yLabel}</span>
          <button
            type="button"
            className="peak-trends-select__sheet-close"
            onClick={() => setOpen(false)}
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>
        </div>
        <ul className="peak-trends-select__list" role="listbox" aria-label={a11yLabel}>
          {groupedOptions.map((row, idx) => {
            if (row.kind === 'heading') {
              return (
                <li key={`h-${row.text}-${idx}`} className="peak-trends-select__group" aria-hidden>
                  {row.text}
                </li>
              );
            }
            const { option } = row;
            const active = option.value === value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`peak-trends-select__item${active ? ' peak-trends-select__item--active' : ''}`}
                  onClick={() => handlePick(option.value)}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      </SlideUpOverlay>
    </>
  );
}
